import { setSidebarNavTourId } from "@bcc/components/tour/tour-ids";
import { useRBAC } from "@bcc/contexts/rbac-context";
import { useWaitingOrderCounts } from "@bcc/hooks/use-waiting-order-counts";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { cn } from "@bcc/lib/utils";
import type { AccessibleModule } from "@shared/types";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import * as Vendor from "@client-shared/providers/vendor";
import type { LucideIcon } from "lucide-react";
import { Bell, CreditCard, DollarSign, Globe, Home, LayoutDashboard, Mail, Package, Settings, ShoppingCart } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { setAppSidebarNavItemTestId, setAppSidebarTestId } from "./app-sidebar.utils";
import { CarrierNavItems } from "./orders/carrier/carrier-nav-items";
import { OrdersCountBadge } from "./orders/orders-count-badge";

/** Navigation item configuration */
interface NavItem {
  /** Translation key for the name */
  nameKey: string;
  /** Route path */
  href: string;
  /** Icon component */
  icon: LucideIcon;
  /** Optional badge count */
  badge?: number;
  /** Required menu group (from RBAC) */
  module: AccessibleModule;
  /** When set, the item is only visible if this page exists in the API response pages array */
  page?: string;
  /** When true, the item is only visible for Breville Roaster Admin users */
  adminOnly?: boolean;
}

/** Static navigation items (Orders href is computed dynamically) */
const ORDERS_NAV_KEY = "sidebar.navigation.orders";

const staticNavigation: NavItem[] = [
  {
    nameKey: "sidebar.navigation.home",
    href: ROUTE_PATHS.HOME,
    icon: Home,
    module: "home",
  },
  {
    nameKey: ORDERS_NAV_KEY,
    href: ROUTE_PATHS.ORDERS,
    icon: ShoppingCart,
    module: "orders",
  },
  {
    nameKey: "sidebar.navigation.batches",
    href: ROUTE_PATHS.BATCHES,
    icon: Package,
    module: "batch",
  },
  {
    nameKey: "sidebar.navigation.products",
    href: ROUTE_PATHS.PRODUCTS,
    icon: Package,
    module: "products",
  },
  {
    nameKey: "sidebar.navigation.payments",
    href: ROUTE_PATHS.FINANCE_INVOICES,
    icon: CreditCard,
    module: "finance",
  },
];

const bonusNavigation: NavItem[] = [
  {
    nameKey: "sidebar.navigation.dashboard",
    href: ROUTE_PATHS.DASHBOARD,
    icon: LayoutDashboard,
    module: "dashboard",
  },
  {
    nameKey: "sidebar.navigation.crossBorder",
    href: ROUTE_PATHS.ADMIN_CROSS_BORDER,
    icon: Globe,
    module: "dashboard",
    adminOnly: true,
  },
  {
    nameKey: "sidebar.navigation.notifications",
    href: ROUTE_PATHS.NOTIFICATIONS,
    icon: Bell,
    module: "notifications",
  },
  {
    nameKey: "sidebar.navigation.financeSettings",
    href: ROUTE_PATHS.FINANCE_SETTINGS,
    icon: DollarSign,
    module: "finance",
    page: "finance.settings",
  },
];

const settingsNavigation: NavItem[] = [
  {
    nameKey: "sidebar.navigation.preferences",
    href: ROUTE_PATHS.PREFERENCES,
    icon: Settings,
    module: "settings",
  },
];

const moduleAliases: Partial<Record<AccessibleModule, AccessibleModule[]>> = {
  products: ["product"],
  product: ["products"],
  batches: ["batch"],
  batch: ["batches"],
  orders: ["order"],
  order: ["orders"],
};

interface AppSidebarProps {
  readonly testId?: string;
}

export function AppSidebar({ testId }: Readonly<AppSidebarProps>) {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  const { hasModuleAccess, hasPageAccess, isLoading } = useRBAC();
  const [authState] = useSFDCAuthState();
  const [vendorState] = Vendor.useVendorContext();
  const { customerAccountCarrierDetails } = vendorState;

  const { waitingCounts, totalCount } = useWaitingOrderCounts(vendorState.currentVendor?.vendorKey, customerAccountCarrierDetails);

  /** Dynamic orders path based on first carrier account */
  const ordersPath = useMemo(() => {
    const defaultCarrier = customerAccountCarrierDetails?.[0];
    return defaultCarrier ? `${ROUTE_PATHS.MARKETPLACE}/${defaultCarrier.id}/orders` : ROUTE_PATHS.ORDERS;
  }, [customerAccountCarrierDetails]);

  /** Navigation with dynamic orders href + waiting-order badge */
  const navigation: NavItem[] = useMemo(
    () =>
      staticNavigation.map((item) => {
        if (item.nameKey !== ORDERS_NAV_KEY) return item;
        return { ...item, href: ordersPath, badge: totalCount || undefined };
      }),
    [ordersPath, totalCount]
  );

  /**
   * Nav visibility is driven by menu group permissions.
   */
  const hasAnyModuleAccess = (module: AccessibleModule) => {
    const candidates = [module, ...(moduleAliases[module] ?? [])];
    return candidates.some((candidate) => hasModuleAccess(candidate));
  };

  const filterNavItems = (items: NavItem[]) =>
    items.filter((item) => {
      if (item.adminOnly && !authState.isRoasterAdmin) return false;
      if (!hasAnyModuleAccess(item.module)) return false;
      if (item.page) return hasPageAccess(item.page);
      return true;
    });

  const visibleNavigation = filterNavItems(navigation);
  const visibleBonusNavigation = filterNavItems(bonusNavigation);
  const visibleSettingsNavigation = filterNavItems(settingsNavigation);

  if (isLoading) {
    return (
      <aside
        data-testid={testId ?? setAppSidebarTestId("loading")}
        className="fixed left-0 top-[72px] z-40 h-[calc(100vh-72px)] w-[264px] border-r border-border-subtle bg-surface-2"
      >
        <nav className="flex h-full flex-col gap-2 p-4">
          <div className="flex-1 space-y-1">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-muted/20" />
            ))}
          </div>
        </nav>
      </aside>
    );
  }

  const renderNavItem = (item: NavItem) => {
    const isOrdersNav = item.nameKey === ORDERS_NAV_KEY;
    const isBatchesLink = item.href === ROUTE_PATHS.BATCHES;

    const isOnOrdersPage = pathname === ROUTE_PATHS.ORDERS || pathname.startsWith(`${ROUTE_PATHS.ORDERS}/`);
    const isActive =
      (isOrdersNav && (pathname.includes(`${ROUTE_PATHS.MARKETPLACE}/`) || isOnOrdersPage)) ||
      (isBatchesLink && pathname === ROUTE_PATHS.BATCHES) ||
      (!isOrdersNav && !isBatchesLink && (pathname === item.href || (item.href !== "/" && pathname.startsWith(item.href))));

    const name = t(item.nameKey) as string;

    return (
      <Link
        key={item.nameKey}
        to={item.href}
        data-testid={setAppSidebarNavItemTestId(name)}
        data-tour-id={setSidebarNavTourId(item.module)}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
          "hover:bg-accent/10",
          isActive
            ? "bg-accent/10 text-accent-strong border-l-4 border-accent-strong -ml-4 pl-[calc(0.75rem-4px)]"
            : "text-muted-foreground hover:text-foreground"
        )}
      >
        <item.icon className="h-5 w-5 shrink-0" strokeWidth={2} />
        <span>{name}</span>
        {item.badge != null && item.badge > 0 && <OrdersCountBadge count={item.badge} />}
      </Link>
    );
  };

  return (
    <aside
      data-testid={testId ?? setAppSidebarTestId("container")}
      className="fixed left-0 top-[72px] z-40 h-[calc(100vh-72px)] w-[264px] border-r border-border-subtle bg-surface-2"
    >
      <nav className="flex h-full flex-col gap-2 p-4">
        <div className="flex-1 space-y-1">
          {visibleNavigation.map((item) => (
            <div key={item.nameKey}>
              {renderNavItem(item)}
              {item.nameKey === ORDERS_NAV_KEY && customerAccountCarrierDetails && (
                <CarrierNavItems carrierDetails={customerAccountCarrierDetails} waitingCounts={waitingCounts} />
              )}
            </div>
          ))}
        </div>

        {visibleBonusNavigation.length > 0 && (
          <div className="border-t border-border-subtle pt-4">
            <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("sidebar.sections.bonus")}
            </div>
            <div className="space-y-1">{visibleBonusNavigation.map(renderNavItem)}</div>
          </div>
        )}

        {visibleSettingsNavigation.length > 0 && (
          <div className="border-t border-border-subtle pt-4">
            <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("sidebar.sections.settings")}
            </div>
            <div className="space-y-1">{visibleSettingsNavigation.map(renderNavItem)}</div>
          </div>
        )}

        <div className="border-t border-border-subtle pt-4">
          <Link
            to={ROUTE_PATHS.CONTACT_US}
            data-testid={setAppSidebarNavItemTestId(t("sidebar.navigation.contactUs") as string)}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              "hover:bg-accent/10",
              pathname === ROUTE_PATHS.CONTACT_US
                ? "bg-accent/10 text-accent-strong border-l-4 border-accent-strong -ml-4 pl-[calc(0.75rem-4px)]"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Mail className="h-5 w-5 shrink-0" strokeWidth={2} />
            <span>{t("sidebar.navigation.contactUs")}</span>
          </Link>
        </div>
      </nav>
    </aside>
  );
}
